import { useEffect, useRef, useState } from 'react';
import ContentItemsPanel from './ui/ContentItemsPanel';
import useRightPanelIndividualCall from './useRightPanelIndividualCall';
import getUrlParam, { decodeUrlQueryValue } from 'components/Dashboard/utilities/getUrlParam';
import { useChartDataOptionSelected } from './ui/MentionsByTopic/ChartDataTable';
import { writtenQuestionCountryByLabel } from './hooks/useCompileMentionsDataFunctions';

const MentionsRightPanel = (props) => {
  const { contentState, analyticsState, setRightPanelState, createSectionValues, stakeholderId, afterLoadingResults } =
    props;
  const { text, dataKey, noResults } = contentState;

  const { chartOptionName } = useChartDataOptionSelected();
  const totalCell = dataKey === 'totalCell';
  const contributionGraph = chartOptionName === 'Contribution type' || chartOptionName === 'Overall';

  const sectionValues = createSectionValues(contentState);
  const [activeSection, setActiveSection] = useState(
    contributionGraph && !totalCell ? dataKey : sectionValues[0]?.name
  );
  const [isLoading, setIsLoading] = useState(!noResults);
  const [results, setResults] = useState([]);

  const callContentItems = useRef(null);
  const pageSize = stakeholderId ? 50 : parseInt(import.meta.env.VITE_APP_PAGE_SIZE);
  const isLoadedComponent = useRef(false);
  const searchParameter = getUrlParam('search');

  const { individualApiCall } = useRightPanelIndividualCall({ analyticsState, contentState });

  callContentItems.current = async ({ type = activeSection, pageNumber, source }) => {
    try {
      if (noResults) {
        setResults({ hits: [] });
        setIsLoading(false);
      } else {
        if (!pageNumber) {
          setIsLoading(true);
        }
        let snapshot = !!contentState?.sentiment;
        let callresults = await individualApiCall({
          type,
          country: writtenQuestionCountryByLabel[type],
          pageNumber,
          snapshot,
          freeSearch: !!searchParameter ? decodeUrlQueryValue(searchParameter) : null,
          source,
          stakeholderId,
        });
        if (callresults) {
          let hits = pageNumber ? results.hits : [];
          let highlights = pageNumber ? results.highlights : [];
          let hitsHighlighted =
            callresults?.highlights?.length > 0
              ? callresults?.hits?.map((item, index) => {
                  return {
                    ...item,
                    highlights: callresults?.highlights[index],
                  };
                })
              : callresults.hits;

          highlights = highlights.concat(callresults.highlights);
          hits = hits.concat(hitsHighlighted);
          setResults({ ...callresults, hits, highlights, thereIsMoreContent: callresults.hits?.length === pageSize });
          setIsLoading(false);
          if (afterLoadingResults) {
            afterLoadingResults();
          }
        }
      }
      isLoadedComponent.current = true;
    } catch (error) {}
  };

  useEffect(() => {
    const source = new AbortController();
    callContentItems.current({ source });
    return () => {
      source.abort('Content items panel cancelled by the user');
    };
  }, [activeSection]);

  useEffect(() => {
    const source = new AbortController();
    if (isLoadedComponent.current) {
      let valueToCompare = contributionGraph && !totalCell ? dataKey : sectionValues[0]?.name;
      if (valueToCompare !== activeSection) {
        setActiveSection(valueToCompare);
      } else {
        callContentItems.current({ source });
      }
    }
    return () => {
      source.abort('Content items panel cancelled by the user');
    };
  }, [contentState]);

  const load_more_results = async () => {
    let { thereIsMoreContent, pageNumber } = results;
    if (thereIsMoreContent) {
      await callContentItems.current({ pageNumber: pageNumber + 1 });
    }
  };
  const handleScroll = (scrollValues) => {
    const { scrollTop, clientHeight, scrollHeight } = scrollValues;
    const scrolledToBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight;
    if (scrolledToBottom) {
      load_more_results();
    }
  };

  return (
    <ContentItemsPanel
      handleScroll={handleScroll}
      setRightPanelState={setRightPanelState}
      text={text}
      sectionsValues={sectionValues}
      isLoading={isLoading}
      activeSection={activeSection}
      setActiveSection={setActiveSection}
      results={results}
      stakeholderId={stakeholderId}
    />
  );
};

export default MentionsRightPanel;
